import { useLogto } from '@logto/react';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { createApiClient, type AvailabilitySlotView, type Tour } from '../api/client';
import { ErrorBanner } from '../ui/ErrorBanner';
import { formatDateTime, formatEur } from '../ui/format';

export function Tours() {
  const { getAccessToken } = useLogto();
  const api = useMemo(() => createApiClient(getAccessToken), [getAccessToken]);
  const [tours, setTours] = useState<Tour[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('30');
  const [bokunId, setBokunId] = useState('');
  const [availTourId, setAvailTourId] = useState('');
  const [slots, setSlots] = useState<AvailabilitySlotView[]>([]);
  const [slotStart, setSlotStart] = useState('');
  const [slotCap, setSlotCap] = useState('12');
  const [pubTourId, setPubTourId] = useState('');
  const [pubTitlePt, setPubTitlePt] = useState('');
  const [pubTitleEn, setPubTitleEn] = useState('');
  const [pubDescPt, setPubDescPt] = useState('');
  const [pubDescEn, setPubDescEn] = useState('');
  const [published, setPublished] = useState(false);
  const [pubSaved, setPubSaved] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setTours(await api.getTours());
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const loadSlots = useCallback(
    async (tid: string) => {
      if (!tid) {
        setSlots([]);
        return;
      }
      try {
        setSlots(await api.getAvailability(tid));
      } catch (err) {
        setError((err as Error).message);
      }
    },
    [api]
  );

  useEffect(() => {
    void loadSlots(availTourId);
  }, [availTourId, loadSlots]);

  async function addTour(e: FormEvent) {
    e.preventDefault();
    try {
      await api.createTour({ title, bokunProductId: bokunId || null, basePriceEur: Number(price) });
      setTitle('');
      setBokunId('');
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function addSlot(e: FormEvent) {
    e.preventDefault();
    try {
      await api.createAvailability(availTourId, { startsAt: `${slotStart}:00Z`, capacity: Number(slotCap) });
      setSlotStart('');
      await loadSlots(availTourId);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  function selectPublicTour(id: string) {
    setPubTourId(id);
    setPubSaved(null);
    const t = tours.find((x) => x.id === id);
    setPubTitlePt(t?.publicTitlePt ?? '');
    setPubTitleEn(t?.publicTitleEn ?? '');
    setPubDescPt(t?.publicDescriptionPt ?? '');
    setPubDescEn(t?.publicDescriptionEn ?? '');
    setPublished(t?.published ?? false);
  }

  async function savePublic(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPubSaved(null);
    try {
      await api.updateTourPublicContent(pubTourId, {
        publicTitlePt: pubTitlePt.trim() || null,
        publicTitleEn: pubTitleEn.trim() || null,
        publicDescriptionPt: pubDescPt.trim() || null,
        publicDescriptionEn: pubDescEn.trim() || null,
        published
      });
      setPubSaved('Conteúdo público guardado.');
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <section className="page">
      <header className="page-head">
        <h1>Tours</h1>
        <p className="subtle">O catálogo e a disponibilidade do operador.</p>
      </header>
      <ErrorBanner message={error} />
      <div className="two-col">
        <section className="card">
          <h2>Catálogo</h2>
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Tour</th>
                  <th className="num">Preço base</th>
                  <th>Bokun</th>
                </tr>
              </thead>
              <tbody>
                {tours.map((t) => (
                  <tr key={t.id}>
                    <td>{t.title}</td>
                    <td className="num money">{formatEur(t.basePriceEur)}</td>
                    <td className="subtle">{t.bokunProductId ?? '—'}</td>
                  </tr>
                ))}
                {tours.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="subtle">Ainda sem tours.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <form className="inline-form" onSubmit={addTour}>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="título do tour" required />
            <input type="number" min="5" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="preço €" required />
            <input value={bokunId} onChange={(e) => setBokunId(e.target.value)} placeholder="Bokun id (opcional)" />
            <button className="btn btn-primary" type="submit">Adicionar tour</button>
          </form>
        </section>

        <section className="card">
          <h2>Disponibilidade</h2>
          <select value={availTourId} onChange={(e) => setAvailTourId(e.target.value)}>
            <option value="">escolher tour…</option>
            {tours.map((t) => (
              <option key={t.id} value={t.id}>{t.title}</option>
            ))}
          </select>
          {availTourId ? (
            <>
              <div className="table-scroll">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Horário</th>
                      <th className="num">Livres</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slots.map((s) => (
                      <tr key={s.startsAt}>
                        <td>{formatDateTime(s.startsAt)}</td>
                        <td className="num">{s.remaining}/{s.capacity}</td>
                      </tr>
                    ))}
                    {slots.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="subtle">Sem horários.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              <form className="inline-form" onSubmit={addSlot}>
                <input type="datetime-local" value={slotStart} onChange={(e) => setSlotStart(e.target.value)} required />
                <input type="number" min="1" value={slotCap} onChange={(e) => setSlotCap(e.target.value)} placeholder="capacidade" required />
                <button className="btn btn-primary" type="submit">Adicionar horário</button>
              </form>
            </>
          ) : null}
        </section>
      </div>

      <section className="card">
        <h2>Página pública</h2>
        <p className="subtle">Títulos e descrições em PT e EN; só aparece no site quando publicado e com ambos os títulos.</p>
        <select value={pubTourId} onChange={(e) => selectPublicTour(e.target.value)} aria-label="Tour para conteúdo público">
          <option value="">escolher tour…</option>
          {tours.map((t) => (
            <option key={t.id} value={t.id}>{t.title}{t.published ? ' (publicado)' : ''}</option>
          ))}
        </select>
        {pubTourId ? (
          <form className="pub-form" onSubmit={savePublic}>
            <label className="field"><span>Título público (PT)</span>
              <input value={pubTitlePt} maxLength={120} onChange={(e) => setPubTitlePt(e.target.value)} /></label>
            <label className="field"><span>Título público (EN)</span>
              <input value={pubTitleEn} maxLength={120} onChange={(e) => setPubTitleEn(e.target.value)} /></label>
            <label className="field"><span>Descrição (PT)</span>
              <textarea value={pubDescPt} maxLength={2000} rows={3} onChange={(e) => setPubDescPt(e.target.value)} /></label>
            <label className="field"><span>Descrição (EN)</span>
              <textarea value={pubDescEn} maxLength={2000} rows={3} onChange={(e) => setPubDescEn(e.target.value)} /></label>
            <label className="check">
              <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} /> Publicado no site
            </label>
            <button className="btn btn-primary" type="submit">Guardar conteúdo público</button>
            {pubSaved ? <div className="alert-ok" role="status">{pubSaved}</div> : null}
          </form>
        ) : null}
      </section>
    </section>
  );
}
